// Shared device-local appearance state. Owns theme resolution and browser chrome;
// native.ts observes the resolved DOM state, while controls only change preference.
import { useSyncExternalStore } from 'react';

export type Theme = 'system' | 'light' | 'dark';
export type ResolvedTheme = Exclude<Theme, 'system'>;
const STORAGE_KEY = 'spent-tracker:theme';
const listeners = new Set<() => void>();

function readStored(): Theme {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

const systemScheme = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  ? window.matchMedia('(prefers-color-scheme: dark)') : null;

function resolve(theme: Theme): ResolvedTheme {
  return theme === 'dark' || (theme === 'system' && systemScheme?.matches) ? 'dark' : 'light';
}

function applyAppearance(theme: Theme, resolvedTheme: ResolvedTheme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolvedTheme === 'dark');
  root.dataset.theme = theme;
  root.dataset.resolvedTheme = resolvedTheme;
  root.style.colorScheme = resolvedTheme;
  root.style.backgroundColor = resolvedTheme === 'dark' ? '#111827' : '#EDF3FA';
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', resolvedTheme === 'dark' ? '#111827' : '#123A8C');
}

let snapshot = { theme: readStored(), resolvedTheme: 'light' as ResolvedTheme };
snapshot = { theme: snapshot.theme, resolvedTheme: resolve(snapshot.theme) };
applyAppearance(snapshot.theme, snapshot.resolvedTheme);

function update(theme: Theme) {
  const resolvedTheme = resolve(theme);
  applyAppearance(theme, resolvedTheme);
  if (theme === snapshot.theme && resolvedTheme === snapshot.resolvedTheme) return;
  snapshot = { theme, resolvedTheme };
  listeners.forEach(listener => listener());
}

function setTheme(theme: Theme) {
  // Storage denial must not stop this session's appearance controls.
  try { window.localStorage.setItem(STORAGE_KEY, theme); } catch { /* In-memory preference remains authoritative. */ }
  update(theme);
}

function onSystemChange() { update(snapshot.theme); }
function onStorage(event: StorageEvent) {
  if (event.key === STORAGE_KEY || event.key === null) update(readStored());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) {
    systemScheme?.addEventListener('change', onSystemChange);
    window.addEventListener('storage', onStorage);
    // Re-resolve if the OS changed while no theme consumers were mounted.
    update(snapshot.theme);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      systemScheme?.removeEventListener('change', onSystemChange);
      window.removeEventListener('storage', onStorage);
    }
  };
}

function getSnapshot() { return snapshot; }

export function useTheme() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { ...state, setTheme };
}
